import { useState } from 'react'

import { api } from '../api/client'
import type { Channel, Page } from '../api/types'
import { Card, Empty, ErrorNote, Spinner, TableScroll } from '../components/ui'
import { useAction, useQuery } from '../hooks/useApi'
import { useAuth } from '../auth/AuthContext'

type ChannelType = Channel['channel_type']

const TYPES: ChannelType[] = ['telegram', 'email', 'webhook', 'mts_link', 'script']

/** Поля конфигурации и секретов, которые ждёт плагин на бэкенде. */
const FIELDS: Record<ChannelType, { config: string[]; secrets: string[] }> = {
  telegram: {
    config: ['chat_id', 'actions_base_url', 'proxy', 'proxy_username'],
    secrets: ['bot_token', 'proxy_password'],
  },
  email: { config: ['to', 'subject_prefix'], secrets: [] },
  webhook: {
    config: ['url', 'method', 'proxy', 'proxy_username'],
    secrets: ['bearer_token', 'proxy_password'],
  },
  mts_link: {
    config: ['url', 'proxy', 'proxy_username'],
    secrets: ['bearer_token', 'proxy_password'],
  },
  script: { config: ['script_path'], secrets: [] },
}

/** Подсказки под полями, значение которых не очевидно из названия. */
const HINTS: Record<string, string> = {
  proxy:
    'пусто — как у всех, none — напрямую, http://…, socks5://… или vless://uuid@host:443?…',
  proxy_username: 'логин к прокси; пароль — в поле proxy_password ниже',
}

interface TelegramSetup {
  configured: boolean
  detail: string
  webhook_url: string
  secret_token: string
  setup_command: string
}

export function ChannelsPage() {
  const { can } = useAuth()
  const channels = useQuery(() => api.get<Page<Channel>>('/channels', { limit: 200 }), [])
  const action = useAction()

  const [type, setType] = useState<ChannelType>('telegram')
  const [name, setName] = useState('')
  const [config, setConfig] = useState<Record<string, string>>({})
  const [secretValues, setSecretValues] = useState<Record<string, string>>({})
  const [fallback, setFallback] = useState('')
  const [health, setHealth] = useState<Record<string, string>>({})
  const [setup, setSetup] = useState<TelegramSetup | null>(null)

  const writable = can('integrations.manage')

  const create = async () => {
    const parsedConfig: Record<string, unknown> = { ...config }
    // Список получателей письма приходит из одного поля через запятую.
    if (type === 'email' && typeof parsedConfig.to === 'string') {
      parsedConfig.to = String(parsedConfig.to)
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
    }
    const ok = await action.run(() =>
      api.post('/channels', {
        name,
        channel_type: type,
        config: parsedConfig,
        secrets: secretValues,
        fallback_channel_id: fallback || null,
        is_active: true,
      }),
    )
    if (ok) {
      setName('')
      setConfig({})
      setSecretValues({})
      channels.reload()
    }
  }

  const check = async (channel: Channel) => {
    await action.run(async () => {
      const result = await api.post<{ status?: string; detail?: string }>(
        `/channels/${channel.id}/health`,
      )
      setHealth({ ...health, [channel.id]: result.status ?? 'unknown' })
    })
  }

  const remove = async (channel: Channel) => {
    if (await action.run(() => api.del(`/channels/${channel.id}`))) channels.reload()
  }

  const configureCallbacks = async (channel: Channel) => {
    await action.run(async () => {
      setSetup(
        await api.post<TelegramSetup>(`/channels/${channel.id}/telegram/webhook-setup`, {
          // Адрес, по которому платформу видно снаружи: за nginx сервер знает
          // о себе только 127.0.0.1, а Telegram нужен публичный.
          base_url: window.location.origin,
        }),
      )
    })
  }

  return (
    <>
      <ErrorNote>{channels.error ?? action.error}</ErrorNote>

      {setup && (
        <Card
          title="Кнопки и команды в Telegram"
          actions={
            <button className="btn-ghost" onClick={() => setSetup(null)}>
              Скрыть
            </button>
          }
        >
          <div className="p-4 space-y-2 text-sm">
            {setup.configured ? (
              <>
                <p className="text-emerald-400">Готово: вебхук установлен боту.</p>
                <p className="text-slate-400">
                  Дежурный подтверждает инциденты кнопкой прямо в чате. Команды бота:{' '}
                  <code className="font-mono text-xs">/id</code> — идентификатор чата,{' '}
                  <code className="font-mono text-xs">/help</code> — справка. В группе с включённым
                  privacy mode команду пишут как <code className="font-mono text-xs">/id@бот</code>.
                </p>
              </>
            ) : (
              <>
                <p className="text-amber-400">
                  Telegram не принял настройку: {setup.detail}. Выполните команду вручную с машины,
                  у которой есть доступ к api.telegram.org.
                </p>
                <code className="block bg-ink-900 border border-ink-600 rounded p-2 font-mono text-xs break-all">
                  {setup.setup_command}
                </code>
              </>
            )}
            <p className="text-xs text-slate-500 break-all">Адрес вебхука: {setup.webhook_url}</p>
          </div>
        </Card>
      )}

      <Card title="Каналы доставки">
        {channels.loading && !channels.data ? (
          <Spinner />
        ) : (channels.data?.items.length ?? 0) === 0 ? (
          <Empty>Каналов нет — уведомления некуда отправлять.</Empty>
        ) : (
          <TableScroll>
            <thead>
              <tr>
                <th className="th">Название</th>
                <th className="th">Тип</th>
                <th className="th">Fallback</th>
                <th className="th">Здоровье</th>
                <th className="th" />
              </tr>
            </thead>
            <tbody>
              {channels.data!.items.map((channel) => (
                <tr key={channel.id} className={channel.is_active ? '' : 'opacity-50'}>
                  <td className="td">{channel.name}</td>
                  <td className="td text-slate-400">{channel.channel_type}</td>
                  <td className="td text-slate-400">
                    {channels.data!.items.find((c) => c.id === channel.fallback_channel_id)?.name ??
                      '—'}
                  </td>
                  <td className="td text-xs">
                    {health[channel.id] ?? channel.health_status}
                  </td>
                  <td className="td text-right">
                    <div className="flex gap-1 justify-end">
                      <button className="btn-ghost text-xs" onClick={() => check(channel)}>
                        Проверить
                      </button>
                      {writable && channel.channel_type === 'telegram' && (
                        <button
                          className="btn-ghost text-xs"
                          onClick={() => configureCallbacks(channel)}
                        >
                          Кнопки в чате
                        </button>
                      )}
                      {writable && (
                        <button className="btn-danger text-xs" onClick={() => remove(channel)}>
                          Удалить
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableScroll>
        )}
      </Card>

      {writable && (
        <Card
          title="Новый канал"
          actions={
            <button className="btn-primary" disabled={action.pending || !name} onClick={create}>
              Создать
            </button>
          }
        >
          <div className="p-4 grid lg:grid-cols-3 gap-4">
            <div>
              <label className="label">Название</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="label">Тип</label>
              <select
                className="input"
                value={type}
                onChange={(e) => {
                  setType(e.target.value as ChannelType)
                  setConfig({})
                  setSecretValues({})
                }}
              >
                {TYPES.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Fallback-канал</label>
              <select
                className="input"
                value={fallback}
                onChange={(e) => setFallback(e.target.value)}
              >
                <option value="">—</option>
                {channels.data?.items.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    {channel.name}
                  </option>
                ))}
              </select>
            </div>

            {FIELDS[type].config.map((field) => (
              <div key={field}>
                <label className="label">{field}</label>
                <input
                  className="input"
                  value={config[field] ?? ''}
                  onChange={(e) => setConfig({ ...config, [field]: e.target.value })}
                />
                {HINTS[field] && <p className="text-xs text-slate-500 mt-1">{HINTS[field]}</p>}
              </div>
            ))}
            {FIELDS[type].secrets.map((field) => (
              <div key={field}>
                <label className="label">{field} (секрет)</label>
                <input
                  className="input"
                  type="password"
                  value={secretValues[field] ?? ''}
                  onChange={(e) => setSecretValues({ ...secretValues, [field]: e.target.value })}
                />
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  )
}
